import { apiClient } from '@/api/client'
import type { ApiSuccessResponse } from '@/types/api'

export const ACADEMIC_PHASES = [
  '2022-2023',
  '2023-2024',
  '2024-2025',
  '2025-2026',
  '2026-2027',
] as const

export type AcademicPhaseValue = (typeof ACADEMIC_PHASES)[number]

export interface ProgramPhase {
  academic_phase: string
  organization_count: number
  active_count: number
}

export interface Program {
  id: number
  name: string
  code: string
  phases: ProgramPhase[]
}

export interface PhaseOrganization {
  id: number
  name: string
  status: string
  mapped: boolean
  is_active: boolean
}

export interface OrgLmsMapping {
  id: number
  lms_source_id: number
  academic_phase: string
  is_active: boolean
}

export interface OrgLmsMappingsResponse {
  organization_id: number
  is_approved: boolean
  lms_sources: {
    id: number
    name: string
    code: string
    phases_from_lms: boolean
    phases: {
      academic_phase: string
      mapped: boolean
      is_active: boolean
      mapping_id: number | null
    }[]
  }[]
}

export interface LmsLivePhase {
  phase_id: string | null
  academic_phase: string
  is_active: boolean
}

export interface LmsLivePhasesResponse {
  lms_source_id: number
  phases: LmsLivePhase[]
  error: string | null
}

export const programsApi = {
  // Phases read live from the client LMS's own phase table (cached server-side).
  lmsPhases: async (lmsSourceId: number, refresh = false): Promise<LmsLivePhasesResponse> => {
    const { data } = await apiClient.get<ApiSuccessResponse<LmsLivePhasesResponse>>(
      `/admin/lms-sources/${lmsSourceId}/phases`,
      { params: refresh ? { refresh: 1 } : undefined },
    )
    return data.data
  },

  list: async (): Promise<Program[]> => {
    const { data } = await apiClient.get<ApiSuccessResponse<Program[]>>('/admin/programs')
    return data.data
  },

  createPhase: async (
    lmsSourceId: number,
    payload: { academic_phase: string; organization_id: number },
  ): Promise<OrgLmsMapping> => {
    const { data } = await apiClient.post<ApiSuccessResponse<OrgLmsMapping>>(
      `/admin/programs/${lmsSourceId}/phases`,
      payload,
    )
    return data.data
  },

  deletePhase: async (lmsSourceId: number, academicPhase: string): Promise<void> => {
    await apiClient.delete(`/admin/programs/${lmsSourceId}/phases/${encodeURIComponent(academicPhase)}`)
  },

  getPhaseOrganizations: async (lmsSourceId: number, academicPhase: string): Promise<PhaseOrganization[]> => {
    const { data } = await apiClient.get<ApiSuccessResponse<{ organizations: PhaseOrganization[] }>>(
      `/admin/programs/${lmsSourceId}/phases/${encodeURIComponent(academicPhase)}/organizations`,
    )
    return data.data.organizations
  },

  syncPhaseOrganizations: async (lmsSourceId: number, academicPhase: string, organizationIds: number[]): Promise<void> => {
    await apiClient.put(
      `/admin/programs/${lmsSourceId}/phases/${encodeURIComponent(academicPhase)}/organizations`,
      { organization_ids: organizationIds },
    )
  },

  // Per-organisation LMS mapping management (used in org offcanvas drawer).
  getOrgMappings: async (organizationId: number): Promise<OrgLmsMappingsResponse> => {
    const { data } = await apiClient.get<ApiSuccessResponse<OrgLmsMappingsResponse>>(
      `/admin/organizations/${organizationId}/lms-mappings`,
    )
    return data.data
  },

  createOrgMapping: async (
    organizationId: number,
    payload: { lms_source_id: number; academic_phase: string },
  ): Promise<OrgLmsMapping> => {
    const { data } = await apiClient.post<ApiSuccessResponse<OrgLmsMapping>>(
      `/admin/organizations/${organizationId}/lms-mappings`,
      payload,
    )
    return data.data
  },

  toggleOrgMapping: async (organizationId: number, mappingId: number): Promise<{ id: number; is_active: boolean }> => {
    const { data } = await apiClient.patch<ApiSuccessResponse<{ id: number; is_active: boolean }>>(
      `/admin/organizations/${organizationId}/lms-mappings/${mappingId}/toggle`,
    )
    return data.data
  },

  deleteOrgMapping: async (organizationId: number, mappingId: number): Promise<void> => {
    await apiClient.delete(`/admin/organizations/${organizationId}/lms-mappings/${mappingId}`)
  },
}
